import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ScholarshipApplicationApiService } from '../../../core/api-services/scholarship-application-api.service';
import { AdminApplication } from '../../../core/models/admin-application';
import { ApplicationDecision } from '../../../core/models/application-decision';
import { ApplicationReview } from '../../../core/models/application-review';
import { PaginationComponent } from '../../../shared/pagination/pagination';

import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import { finalize } from 'rxjs';
import { ConfirmationService } from '../../../core/services/confirmation';

@Component({
  selector: 'app-admin-applications',
  imports: [CommonModule, DatePipe, FormsModule, PaginationComponent],
  templateUrl: './applications.html',
  styleUrl: './applications.scss',
})
export class AdminApplicationsComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly applicationsApi = inject(ScholarshipApplicationApiService);

  private readonly confirmation = inject(ConfirmationService);
  readonly confirming = signal(false);

  readonly auth = inject(AuthorizationService);
  readonly permissions = Permissions;
  readonly selected = signal<ApplicationReview | null>(null);
  readonly detailLoading = signal(false);
  readonly saving = signal(false);
  readonly detailError = signal('');
  readonly successMessage = signal('');
  readonly downloading = signal<number | null>(null);
  selectedId: number | null = null;

  applications: AdminApplication[] = [];
  currentPage = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 25, 50, 100];
  loading = true;
  errorMessage = '';

  ngOnInit(): void {
    this.loadApplications();
  }

  get paginatedApplications(): AdminApplication[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.applications.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.applications.length / this.pageSize));
  }

  get showingFrom(): number {
    return this.applications.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.applications.length);
  }

  loadApplications(): void {
    this.loading = true;
    this.errorMessage = '';
    this.applicationsApi.getApplicationsForReview().subscribe({
      next: applications => {
        this.applications = [...applications].sort((a, b) =>
          new Date(a.applicationDate).getTime() - new Date(b.applicationDate).getTime());
        this.currentPage = 1;
        this.loading = false;
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Applications could not be loaded. Please try again.';
        this.loading = false;
        this.changeDetector.markForCheck();
      },
    });
  }

  changePageSize(): void {
    this.currentPage = 1;
  }

  goToPage(page: number): void {
    this.currentPage = page;
  }

  applicantName(application: AdminApplication): string {
    return [application.user?.firstName, application.user?.lastName].filter(Boolean).join(' ') || 'Unknown applicant';
  }
  openApplication(application: AdminApplication): void {
    this.selectedId = application.id;
    this.selected.set(null);
    this.detailError.set('');
    this.successMessage.set('');
    this.detailLoading.set(true);
    this.applicationsApi.getApplicationReview(application.id).subscribe({
      next: review => {
        if (this.selectedId !== application.id) return;
        this.selected.set(review);
        this.detailLoading.set(false);
      },
      error: () => {
        if (this.selectedId !== application.id) return;
        this.detailError.set('Application details could not be loaded. Return to the list and try again.');
        this.detailLoading.set(false);
      },
    });
  }

  backToList(): void {
    this.selectedId = null;
    this.selected.set(null);
    this.detailLoading.set(false);
    this.detailError.set('');
    this.successMessage.set('');
  }

  get applicationDecided(): boolean {
    const status = this.selected()?.application.applicationStatus?.name?.trim().toUpperCase();
    return status === 'APPROVED' || status === 'DECLINED';
  }

  async reviewApplication(decision: ApplicationDecision): Promise<void> {
    const review = this.selected();
    if (!review || this.saving() || this.confirming() || this.applicationDecided) return;
    const action = {
      Approve: 'Approve',
      Decline: 'Decline',
      HigherReview: 'Send to higher review',
    }[decision];
    this.confirming.set(true);
    let confirmed: boolean;
    try {
      confirmed = await this.confirmation.open({
        title: `${action}?`,
        message: `Please confirm: ${action.toLowerCase()} application ${review.application.referenceNumber} for ${this.applicantName(review.application)}.`,
        confirmText: action,
        cancelText: 'Cancel',
        type: decision === 'Decline' ? 'danger' : decision === 'Approve' ? 'success' : 'warning',
      });
    } finally {
      this.confirming.set(false);
    }
    if (!confirmed || this.selected()?.application.id !== review.application.id || this.saving() || this.applicationDecided) return;
    this.saving.set(true);
    this.detailError.set('');
    this.successMessage.set('');
    this.applicationsApi.reviewApplication(review.application.id, decision)
      .pipe(finalize(() => this.saving.set(false))).subscribe({
        next: updated => this.saved(updated, 'Application decision saved.'),
        error: error => {
          this.detailError.set(error.status === 409
            ? 'This application has already been decided or changed by another reviewer. Reopen it to see the current status.'
            : 'The application decision could not be saved. Please try again.');
        },
      });
  }

  reviewDocument(documentId: number, decision: 'Approved' | 'Rejected'): void {
    const review = this.selected();
    if (!review || this.saving()) return;
    this.saving.set(true);
    this.detailError.set('');
    this.successMessage.set('');
    this.applicationsApi.reviewDocument(review.application.id, documentId, decision)
      .pipe(finalize(() => this.saving.set(false))).subscribe({
        next: updated => this.saved(updated, 'Document decision saved.'),
        error: () => this.detailError.set('The document decision could not be saved. Please try again.'),
      });
  }

  private saved(review: ApplicationReview, message: string): void {
    this.selected.set(review);
    this.applications = this.applications.map(application =>
      application.id === review.application.id ? review.application : application);
    this.successMessage.set(message);
  }

  downloadDocument(documentId: number, name: string): void {
    const review = this.selected();
    if (!review || this.downloading() !== null) return;
    this.downloading.set(documentId);
    this.detailError.set('');
    this.applicationsApi.downloadApplicationDocument(review.application.id, documentId)
      .pipe(finalize(() => this.downloading.set(null))).subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = name;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        error: () => this.detailError.set('The document could not be downloaded. Please try again.'),
      });
  }

  displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.map(item => this.displayValue(item)).join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
}
